import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Loader2, RotateCcw, Info, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { TransportChoice, covoitMessage } from "./TransportChoice";
import { NameSuggestions } from "./NameSuggestions";
import { messageCoach } from "./ShareWhatsApp";

const Field = ({ id, label, value, onChange, error }) => (
  <label className="block">
    <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim sm:mb-2">{label} <span className="text-lime">*</span></span>
    <input
      data-testid={`convocation-form-${id}-input`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="off"
      maxLength={60}
      className={`w-full rounded-2xl border bg-ink px-4 py-3 text-base text-chalk outline-none transition-colors duration-300 placeholder:text-dim/60 focus:border-lime ${error ? "border-signal" : "border-line"}`}
      placeholder={label}
    />
    {error && <span data-testid={`convocation-form-${id}-error`} className="mt-2 block text-sm text-signal">{error}</span>}
  </label>
);

const StatusButton = ({ active, kind, onClick }) => {
  const present = kind === "present";
  const Icon = present ? Check : X;
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.96 }}
      data-testid={`convocation-form-${kind}-button`}
      aria-pressed={active}
      onClick={onClick}
      className={`relative flex items-center justify-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 font-display text-2xl uppercase transition-colors duration-300 ${
        active ? (present ? "border-lime bg-lime text-ink" : "border-signal bg-signal text-ink") : "border-line bg-ink text-chalk hover:border-chalk/50"
      }`}
    >
      <Icon className="h-6 w-6" strokeWidth={3} />
      {present ? "Présent" : "Absent"}
    </motion.button>
  );
};

const MOTIVATION = [
  "💪 Donne le meilleur de toi-même à chaque instant.",
  "🔥 Bats-toi sur chaque ballon, joue avec le cœur, la détermination et l'envie de gagner !",
  "🤝 Unis sur le terrain, solidaires dans l'effort, forts ensemble !",
];

const MOTIVATION_ENTRAINEMENT = [
  "👂 Écoute bien les consignes du coach et applique-les.",
  "💪 Donne-toi à fond sur chaque exercice, c'est ici que tu progresses.",
  "🤝 Encourage tes coéquipiers : on avance ensemble.",
];

const CHECKLIST = ["Ta carte d'identité", "Tes protège-tibias", "Tes chaussettes", "Ton short", "Ta gourde"];

const Motivation = ({ entrainement }) => (
  <motion.div
    data-testid="convocation-motivation"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15, duration: 0.6 }}
    className="w-full rounded-2xl bg-lime p-5 text-ink"
  >
    <p className="font-display text-2xl uppercase leading-tight sm:text-3xl">{entrainement ? "Entraîne-toi comme si c'était un match !" : "Joue chaque match comme si c'était le dernier !"}</p>
    <ul className="mt-3 space-y-1.5 text-sm font-medium sm:text-base">
      {(entrainement ? MOTIVATION_ENTRAINEMENT : MOTIVATION).map((l) => <li key={l}>{l}</li>)}
    </ul>
    <p className="mt-3 font-display text-xl uppercase">{entrainement ? "⚽ On joue en match comme on s'entraîne !" : "🏆 Aucun regret, donne tout !"}</p>
  </motion.div>
);

const LABEL = { present: "Présent", absent: "Absent" };

const DejaReponduDialog = ({ conflict, nouveau, onClose, onConfirm, loading }) => {
  const [raison, setRaison] = useState("");
  const [err, setErr] = useState("");
  const same = conflict?.code === "deja_repondu";
  const confirm = () => {
    if (!raison.trim()) { setErr("La raison du changement est obligatoire"); return; }
    onConfirm(raison.trim());
  };
  return (
    <Dialog open={!!conflict} onOpenChange={(o) => { if (!o) { setRaison(""); setErr(""); onClose(); } }}>
      <DialogContent data-testid="deja-repondu-dialog" className="w-[calc(100%-2rem)] max-w-lg rounded-[24px] border-signal/60 bg-pitch text-chalk">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-display text-3xl uppercase text-signal"><AlertTriangle className="h-7 w-7" />Déjà répondu</DialogTitle>
          <DialogDescription data-testid="deja-repondu-message" className="text-base text-chalk">
            Une réponse existe déjà à ce nom : <strong>{LABEL[conflict?.statut]}</strong>.
            {same ? " Une seule réponse est autorisée par joueur." : ` Pour passer à « ${LABEL[nouveau]} », indique obligatoirement la raison du changement.`}
          </DialogDescription>
        </DialogHeader>
        {!same && (
          <div>
            <span className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-dim">Raison du changement <span className="text-lime">*</span></span>
            <textarea data-testid="changement-raison-textarea" rows={3} maxLength={500} value={raison} onChange={(e) => { setRaison(e.target.value); setErr(""); }} className={`w-full resize-none rounded-2xl border bg-ink px-4 py-3 text-chalk outline-none focus:border-lime ${err ? "border-signal" : "border-line"}`} />
            {err && <span data-testid="changement-raison-error" className="mt-1 block text-sm text-signal">{err}</span>}
          </div>
        )}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button data-testid="deja-repondu-close-button" onClick={onClose} className="rounded-full border border-line px-5 py-3 text-chalk hover:border-chalk/50">{same ? "Compris" : "Annuler"}</button>
          {!same && (
            <button data-testid="changement-confirm-button" disabled={loading} onClick={confirm} className="inline-flex items-center justify-center gap-2 rounded-full bg-lime px-5 py-3 font-semibold text-ink disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}Confirmer le changement
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TransportRecap = ({ data, covoit }) => {
  if (data.statut !== "present" || !data.transport) return null;
  return (
    <p data-testid="convocation-transport-recap" className="w-full rounded-2xl border border-line bg-ink p-4 text-base text-chalk">
      {data.transport === "direct"
        ? "C'est noté : tu y vas directement, sans passer par le club."
        : data.transport === "places"
        ? data.places === 0
          ? "C'est noté : ta voiture est pleine, aucune place libre."
          : `Merci ! Tu as indiqué ${data.places} place${data.places > 1 ? "s" : ""} libre${data.places > 1 ? "s" : ""} dans ta voiture.`
        : covoitMessage(covoit?.lieu, covoit?.heure)}
    </p>
  );
};

const Confirmation = ({ data, type, covoit, onReset }) => (
  <motion.div
    data-testid="convocation-confirmation"
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-start gap-4"
  >
    <div className="flex items-center gap-4">
    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${data.statut === "present" ? "bg-lime" : "bg-signal"} text-ink`}>
      {data.statut === "present" ? <Check className="h-8 w-8" strokeWidth={3} /> : <X className="h-8 w-8" strokeWidth={3} />}
    </span>
    <h3 className="font-display text-4xl uppercase leading-none text-chalk">Merci {data.prenom} !</h3>
    </div>
    {(data.statut === "present" || data.mise_a_jour) && (
      <p className="text-base text-dim">
        {data.statut === "present" && "Ta présence est confirmée. On compte sur toi."}
        {data.mise_a_jour && " Changement enregistré et transmis au coach."}
      </p>
    )}
    <TransportRecap data={data} covoit={covoit} />
    {data.statut === "absent" && (
      <motion.div
        data-testid="convocation-absent-message"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.6 }}
        className="w-full rounded-2xl border border-signal/60 bg-ink p-5 text-chalk"
      >
        <p className="font-display text-2xl uppercase leading-tight text-signal sm:text-3xl">Merci pour ta réponse !</p>
        <p className="mt-2 text-base">Nous te remercions pour ta réponse. On espère te voir au prochain entraînement en forme.</p>
        <p className="mt-2 font-display text-xl uppercase text-lime">On compte sur toi !</p>
      </motion.div>
    )}
    {data.statut === "present" && <Motivation entrainement={type === "entrainement"} />}
    {data.statut === "present" && type !== "entrainement" && (
      <div data-testid="convocation-checklist" className="w-full rounded-2xl border border-lime/40 bg-ink p-5">
        <p className="font-display text-2xl uppercase text-lime">N'oublie pas</p>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {CHECKLIST.map((item, i) => (
            <motion.li key={item} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08 }} className="flex items-center gap-3 text-chalk">
              <Check className="h-4 w-4 shrink-0 text-lime" strokeWidth={3} />{item}
            </motion.li>
          ))}
        </ul>
      </div>
    )}
    <button data-testid="convocation-change-answer-button" onClick={onReset} className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-3 text-chalk transition-colors duration-300 hover:border-lime">
      <RotateCcw className="h-4 w-4" /> Modifier ma réponse
    </button>
  </motion.div>
);

const InfosPratiques = ({ infos }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button type="button" data-testid="infos-pratiques-button" className="inline-flex shrink-0 items-center gap-2 rounded-full border border-lime/60 px-4 py-2 text-sm text-lime transition-colors duration-300 hover:bg-lime hover:text-ink">
        <Info className="h-4 w-4" /> Infos pratiques
      </button>
    </DialogTrigger>
    <DialogContent data-testid="infos-pratiques-dialog" className="w-[calc(100%-2rem)] max-w-lg rounded-[24px] border-line bg-pitch text-chalk">
      <DialogHeader>
        <DialogTitle className="font-display text-4xl uppercase">Infos pratiques</DialogTitle>
        <DialogDescription className="text-dim">À lire avant de répondre.</DialogDescription>
      </DialogHeader>
      <p data-testid="infos-pratiques-content" className="whitespace-pre-line text-base leading-relaxed text-chalk">{infos || "Aucune info pour l'instant."}</p>
    </DialogContent>
  </Dialog>
);

export const ResponseForm = ({ infos, convocationId, covoitLieu, covoitHeure, type }) => {
  const avecTransport = type !== "entrainement";
  const [form, setForm] = useState({ nom: "", prenom: "", statut: null, motif: "", transport: null, places: null });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [conflict, setConflict] = useState(null);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const send = async (raison_changement) => {
    setLoading(true);
    try {
      const present = form.statut === "present";
      const { data } = await api.post("/reponses", {
        ...form, motif: form.statut === "absent" ? form.motif : null, raison_changement, convocation_id: convocationId ?? "",
        transport: present && avecTransport ? form.transport : null, places: present && avecTransport && form.transport === "places" ? form.places : null,
      });
      setConflict(null);
      setDone(data);
      toast.success("Réponse envoyée");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 403 && d?.code === "delai_depasse") toast.error("Délai de réponse dépassé : envoie un message au coach.", { duration: 10000, action: { label: "Message au coach", onClick: () => messageCoach(null) } });
      else if (err?.response?.status === 409 && d?.code) setConflict(d);
      else if (err?.response?.status === 422 && d?.code) toast.error("Indique ton transport (et le nombre de places).");
      else toast.error("Impossible d'envoyer la réponse, réessaie.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom.trim()) errs.nom = "Le nom est obligatoire";
    if (!form.prenom.trim()) errs.prenom = "Le prénom est obligatoire";
    if (!form.statut) errs.statut = "Choisis Présent ou Absent";
    if (form.statut === "present" && avecTransport && !form.transport) errs.transport = "Indique comment tu viens";
    if (form.statut === "present" && avecTransport && form.transport === "club") errs.transport = "Indique si tu as de la place dans ton véhicule";
    if (form.statut === "present" && avecTransport && form.transport === "places" && (form.places === null || form.places === undefined)) errs.transport = "Indique le nombre de places disponibles";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    send(null);
  };

  return (
    <div id="reponse" data-testid="response-section" className="w-full">
      <DejaReponduDialog conflict={conflict} nouveau={form.statut} loading={loading} onClose={() => setConflict(null)} onConfirm={send} />
          <div className="rounded-[24px] border border-line bg-pitch/90 p-4 backdrop-blur-xl sm:p-6">
            {done ? (
              <Confirmation data={done} type={type} covoit={{ lieu: covoitLieu, heure: covoitHeure }} onReset={() => setDone(null)} />
            ) : (
              <form data-testid="convocation-form" onSubmit={submit} noValidate className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-display text-2xl uppercase leading-none text-chalk sm:text-3xl">Ta réponse</h2>
                  {infos && <InfosPratiques infos={infos} />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field id="nom" label="Nom" value={form.nom} onChange={set("nom")} error={errors.nom} />
                  <Field id="prenom" label="Prénom" value={form.prenom} onChange={set("prenom")} error={errors.prenom} />
                  <NameSuggestions convocationId={convocationId} nom={form.nom} prenom={form.prenom}
                    onPick={(j) => { setForm((f) => ({ ...f, nom: j.nom, prenom: j.prenom })); setErrors((e) => ({ ...e, nom: null, prenom: null })); }} />
                </div>
                <div>
                  <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim sm:mb-2">Disponibilité <span className="text-lime">*</span></span>
                  <div className="grid grid-cols-2 gap-3">
                    <StatusButton kind="present" active={form.statut === "present"} onClick={() => set("statut")("present")} />
                    <StatusButton kind="absent" active={form.statut === "absent"} onClick={() => set("statut")("absent")} />
                  </div>
                  {errors.statut && <span data-testid="convocation-form-statut-error" className="mt-2 block text-sm text-signal">{errors.statut}</span>}
                </div>
                <AnimatePresence initial={false}>
                  {form.statut === "present" && avecTransport && (
                    <motion.div key="transport" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                      <TransportChoice type={type} transport={form.transport} places={form.places} onTransport={set("transport")}
                        onPlaces={(n) => { setForm((f) => ({ ...f, places: n })); setErrors((e) => ({ ...e, transport: null })); }}
                        lieu={covoitLieu} heure={covoitHeure} error={errors.transport} />
                    </motion.div>
                  )}
                  {form.statut === "absent" && (
                    <motion.label
                      key="motif"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="block overflow-hidden"
                    >
                      <span className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-dim">Motif de l'absence (facultatif)</span>
                      <textarea
                        data-testid="convocation-form-motif-textarea"
                        value={form.motif}
                        onChange={(e) => set("motif")(e.target.value)}
                        maxLength={500}
                        rows={2}
                        placeholder="Blessure, déplacement, examen..."
                        className="w-full resize-none rounded-2xl border border-line bg-ink px-4 py-3 text-chalk outline-none transition-colors duration-300 placeholder:text-dim/60 focus:border-signal"
                      />
                    </motion.label>
                  )}
                </AnimatePresence>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  data-testid="convocation-form-submit-button"
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-chalk px-8 py-3 text-lg font-semibold text-ink transition-colors duration-300 hover:bg-lime disabled:opacity-60 sm:py-4"
                >
                  {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                  Envoyer ma réponse
                </motion.button>
              </form>
            )}
          </div>
    </div>
  );
};
